import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext';
import { useModifier } from '../contexts/ModifiersContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { productNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddCard from '../components/AddCard';
import PageHeader from '../components/PageHeader';
import AddModifiers from '../components/AddModifiers';
import InfiniteScroll from 'react-infinite-scroll-component';

function ModifiersList() {
    const { getAllModifiers, getSingleModifier } = useModifier()
    const { toast, restaurantId, auth, hasPermission } = useAuth();
    const { currency } = useCompanySetting()

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);

    const [modifiersList, setModifiersList] = useState([])
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editModifierId, setEditModifierId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [modifierDetail, setModifierDetail] = useState({
        name: "",
        display_name: "",
        price: 0,
        modifiers: [],
        restaurant_id: restaurantId
    })
    const [filter, setFilter] = useState({
        limit: 50,
        filter: null,
        restaurant_id: restaurantId,
        totalRenderedRecords: 0,
    });

    useEffect(() => {
        if (restaurantId) {
            handleFilterChange({ ...filter, restaurant_id: restaurantId })
        }
    }, [restaurantId])

    const handleFilterChange = async (newFilter) => {
        setModifiersList([]);
        setFilter({ ...newFilter, totalRenderedRecords: 0, });
        setHasMore(true);
    };

    useEffect(() => {
        if (filter) {
            fetchModifiers(filter)
        }
    }, [filter])

    const fetchModifiers = async (filter) => {
        try {
            if (restaurantId) {
                setIsLoading(true)

                const data = await getAllModifiers(filter)
                if (data.modifiers.length !== 0 && data.totalCount !== modifiersList.length) {
                    setModifiersList([...modifiersList, ...data.modifiers])
                    setTotalRecords(data.totalCount)
                    setTotalRenderedRecords(modifiersList.length)
                }

                setIsLoading(false)
            }
        } catch (error) {
            console.error('Error fetching modifiers', error);
        }
    }

    const handleCardClick = (id) => {
        if(!canUpdate && !canDelete) return;
        setEditMode(true);
        setEditModifierId(id);
        setVisible(false);
    };

    const handleLoadMore = () => {
        if (modifiersList.length === totalRecords) {
            setHasMore(false);
        } else if (modifiersList.length === filter.limit) {
            const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        } else {
            const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        }
    };

    const fetchModifierProps = async (newModifierId) => {
        try {
            if (editMode && editModifierId) {
                const modifier = await getSingleModifier(editModifierId);
                const modifierIndex = modifiersList.findIndex((c) => c.id === editModifierId);

                if (modifierIndex !== -1) {
                    if (!modifier) {
                        const updatedModifierList = [...modifiersList];
                        updatedModifierList.splice(modifierIndex, 1);

                        setModifiersList(updatedModifierList);
                        setTotalRecords(totalRecords - 1);
                        setTotalRenderedRecords(totalRenderedRecords - 1)
                    } else {
                        const updatedModifierList = [...modifiersList];
                        updatedModifierList[modifierIndex] = modifier;

                        setModifiersList(updatedModifierList);
                    }
                } else {
                    setModifiersList([...modifiersList, modifier]);
                }
            } else if (visible) {
                const updatedModifierList = [...modifiersList];
                const formattedPrice = parseFloat(modifierDetail.price).toFixed(2);

                updatedModifierList.unshift({ ...modifierDetail, id: newModifierId, price: formattedPrice });

                setModifiersList(updatedModifierList);
                setTotalRecords(totalRecords + 1);
                setTotalRenderedRecords(totalRenderedRecords + 1)
            }
        } catch (error) {
            console.error('Error fetching modifier', error);
        }
    };

    const renderCardsInRows = () => {
        const rows = [];
        let currentRow = [];
        let count = 0;

        modifiersList.forEach((modifier, index) => {
            currentRow.push(
                <div key={modifier.id} className="d-flex">
                    <Card
                        header={
                            <>
                                <div className='modifier-name' title={modifier.name}>
                                    {modifier.name}
                                </div>
                                <div className='modifier-options'>
                                    {currency}{modifier.price}
                                </div>
                            </>}
                        className="modifiers-list-card"
                        onClick={() => handleCardClick(modifier.id)}
                    />
                </div>
            );
            count++;
            rows.push(
                <div key={index} className="d-flex">
                    {currentRow}
                </div>
            );
            currentRow = [];
            count = 0;
        });

        return rows;
    };

    return (
        <Layout2 items={productNav.items} navTitle={productNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Modifiers'} />
                <div className='px-4 py-2'>
                    {
                        (visible || (editMode && editModifierId)) &&
                        (
                            <AddModifiers
                                id={editModifierId}
                                visible={visible}
                                editModifierId={editModifierId}
                                editMode={editMode}
                                modifierDetail={modifierDetail}
                                setEditModifierId={setEditModifierId}
                                setVisible={setVisible}
                                setEditMode={setEditMode}
                                fetchModifiers={fetchModifierProps}
                                setModifierDetail={setModifierDetail}
                                canCreate={canCreate}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                            />
                        )
                    }
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <Toast ref={toast} />
                            <div className='user-list'>
                                <InfiniteScroll
                                    dataLength={modifiersList.length}
                                    next={handleLoadMore}
                                    hasMore={hasMore}
                                    className="card-container"
                                >
                                    {canCreate && <AddCard color="#536347" label="Add Modifiers" setVisible={setVisible} />}
                                    {modifiersList.length !== 0 && (
                                        renderCardsInRows()
                                    )}
                                </InfiniteScroll>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default ModifiersList